import { trackFullStoryEvent } from "@expert/monitoring";
import { useEffect, useRef } from "react";

import { getRootDispatcher } from "@/analytics";
import { playNotificationSound } from "@/notification/playNotificationSound";

import type { OverlayPhase } from "./warmHandoffPhase";

export function usePhaseTransitionEffects(
    phase: OverlayPhase,
    activeTaskId: string | null,
    handoffRecordingRuntimeMs?: number,
): void {
    const prevPhaseRef = useRef<OverlayPhase>("hidden");
    const prevTaskIdRef = useRef<string | null>(null);
    const activeTaskIdRef = useRef(activeTaskId);
    // oxlint-disable-next-line react/refs
    activeTaskIdRef.current = activeTaskId;

    useEffect(() => {
        const taskChanged = activeTaskId !== prevTaskIdRef.current;
        prevTaskIdRef.current = activeTaskId;

        if (phase === "hidden") {
            prevPhaseRef.current = "hidden";
            return;
        }

        const taskId = activeTaskIdRef.current;
        const isNewEntry = prevPhaseRef.current === "hidden" || taskChanged;

        if (isNewEntry && phase === "standby") {
            void playNotificationSound("warmHandoffConnecting");
            const eventData = { taskId, handoffRecordingRuntimeMs };
            trackFullStoryEvent("WarmHandoffStandbyStarted", eventData);
            void getRootDispatcher().dispatchBusinessEvent("WarmHandoffStandbyStarted", eventData);
        }

        if (phase === "live" && (prevPhaseRef.current !== "live" || taskChanged)) {
            void playNotificationSound("warmHandoffLive");
            const eventData = { taskId, handoffRecordingRuntimeMs };
            trackFullStoryEvent("WarmHandoffLiveStarted", eventData);
            void getRootDispatcher().dispatchBusinessEvent("WarmHandoffLiveStarted", eventData);
        }

        prevPhaseRef.current = phase;
    }, [phase, activeTaskId, handoffRecordingRuntimeMs]);
}
